import React, { useEffect } from 'react'
import ReactDOM from 'react-dom'
import classnames from 'classnames'
import map from 'lodash/map'
import { IDropdownPosition } from './FormatSelection'
import useClickOutsideListener from '@Utils/UseClickOutsideListener'
import Arrow from '@Images/languages_dropdown_arrow.svg'
import Remove from '@Images/project_setup_form_remove_language.svg'

interface FormatProps {
  readonly index: number
  readonly currentFormat: string
  readonly selectedFormats: string[]
  readonly indexForReplacingFormat: number
  readonly dropdownOffsetY: number
  readonly availableFormats: string[]
  setIndexForReplacingFormat(indexForReplacingFormat: number): void
  setSelectedFormats(selectedFormats: string[]): void
}

const Format: React.FC<FormatProps> = props => {
  const {
    index,
    selectedFormats,
    currentFormat,
    dropdownOffsetY,
    indexForReplacingFormat,
    availableFormats,
  } = props

  const shouldShowDropdown = indexForReplacingFormat === index
  const isFirstFormat = index === 0

  useEffect(() => {
    const container = document.getElementsByClassName('ExportationPopup')
    if (container && container.length > 0) {
      const containerElement = container[0] as HTMLElement
      containerElement.style.overflowY = shouldShowDropdown ? 'hidden' : 'scroll'
    }
  }, [shouldShowDropdown])

  const toggleFormats = () => {
    if (indexForReplacingFormat >= 0) {
      props.setIndexForReplacingFormat(-1)
    } else {
      props.setIndexForReplacingFormat(index)
    }
  }

  const handleRemoveFormat = () => {
    if (selectedFormats.length === 1) { return }

    props.setSelectedFormats([...selectedFormats.slice(0, index), ...selectedFormats.slice(index + 1)])
  }

  const handleCloseDropdown = () => {
    props.setIndexForReplacingFormat(-1)
  }

  useClickOutsideListener('FormatSelection__item-main-element', handleCloseDropdown, indexForReplacingFormat !== -1)

  const renderCurrentItem = () => {
    return (
      <div
        className="SearchDropdown__current-item"
        onClick={toggleFormats}
      >
        <div className="SearchDropdown__current-item-name">
          {currentFormat}
        </div>
        <Arrow />
      </div>
    )
  }

  const renderButtons = () => {
    return (
      <div className="SetupProjectPopupTts__buttons">
        {renderRemoveButton()}
      </div>
    )
  }

  const renderRemoveButton = () => {
    return (
      <div
        className="SetupProjectPopupTts__button"
        onClick={handleRemoveFormat}
      >
        <Remove />
      </div>
    )
  }

  const renderFormats = (style: IDropdownPosition, formatIndexToReplace: number) => {
    return ReactDOM.createPortal(
      (
        <ul
          className={classnames(
            'SearchDropdown__items',
            'FormatSelection__item-element',
            { 'FormatSelection__item-element-first': isFirstFormat }
          )}
          style={style}
        >
          {renderFormatsList(formatIndexToReplace)}
        </ul>
      ),
      document.getElementById('site-container')!
    )
  }

  const renderFormatsList = (formatIndexToReplace: number) => {
    return (
      map(availableFormats, (availableFormat, ind) => {
        const alreadySelectedFormat = selectedFormats.includes(availableFormat)
        const isSelectable = !alreadySelectedFormat

        const handleReplaceSelectedFormat = () => {
          if (!isSelectable) { return }

          props.setSelectedFormats([...selectedFormats.slice(0, formatIndexToReplace), availableFormat, ...selectedFormats.slice(formatIndexToReplace + 1)])
          handleCloseDropdown()
        }

        return (
          <li
            key={ind}
            className={classnames(
              'SearchDropdown__item',
              'FormatSelection__item-main-element',
              { 'SearchDropdown__item-selectable': isSelectable }
            )}
            onClick={handleReplaceSelectedFormat}
          >
            <div className="SearchDropdown__item-name">
              {availableFormat}
            </div>
            <div className="SearchDropdown__item-info">
              {alreadySelectedFormat && 'Already added'}
            </div>
          </li>
        )
      })
    )
  }

  const formatDropdownParentElement = document.getElementById(`format-selection-item-${index}`)
  let formatDropdownX = 0
  let formatDropdownY = 0

  if (formatDropdownParentElement) {
    const formatDropdownParentElementBounds = formatDropdownParentElement.getBoundingClientRect()

    formatDropdownX = formatDropdownParentElementBounds.left
    formatDropdownY = formatDropdownParentElementBounds.top + dropdownOffsetY
  }

  const formatDropdownPositioningStyle = {
    top: `${formatDropdownY}px`,
    left: `${formatDropdownX}px`,
  }

  return (
    <li
      className="FormatSelection__item"
      id={`format-selection-item-${index}`}
    >
      <div
        className={classnames(
          'FormatSelection__item-main',
          'FormatSelection__item-main-element',
          { 'FormatSelection__item-main-first': isFirstFormat }
        )}
      >
        {renderCurrentItem()}
        {shouldShowDropdown && renderFormats(formatDropdownPositioningStyle, index)}
        {!shouldShowDropdown && !isFirstFormat && renderButtons()}
      </div>
    </li>
  )
}

export default Format
